/** A 100% stacked bar with legend rows that carry the values: the split of a whole (ADR-0212, P7).
 *
 * This replaces a ring wherever the ring has fewer than three meaningful segments (scope 1 and 2;
 * documents by type with two types): two arcs say less than one bar and a legend, and the headline
 * in a hole competes with the figures. The bar is 14px high with 2px surface gaps between segments;
 * the legend under it has one row per part with its swatch, name, **its value printed directly** and
 * its share, so nothing needs hovering to be read.
 *
 * Segment widths are `value` against the sum of the values (geometry; the printed `text` and `share`
 * are the caller's strings, so nothing is summed or rounded for display here). Each segment is a
 * focusable image named with its sentence, shows a tooltip on hover and on focus, dims the others,
 * and opens its receipt on click or Enter when it has a `receipt` and the caller passes
 * `onOpenReceipt`. A part with no value keeps its legend row (an omission would read as nothing
 * being there) and has no segment. Inside a `ChartCard` the Table button shows the same parts as a
 * table. The bar grows once from the left and is still under reduced motion.
 */

import { motion, useReducedMotion } from "motion/react";
import { useRef, useState } from "react";
import type { KeyboardEvent } from "react";

import chart from "./Chart.module.css";
import { ChartTable } from "./ChartTable";
import { ChartTooltip } from "./ChartTooltip";
import styles from "./StackedShare.module.css";
import { colourOf } from "./chartTokens";
import type { ChartColour } from "./chartTokens";
import { useChartAsTable } from "./chartView";
import { canOpen, receiptProps } from "./receipt";
import type { OpenReceipt } from "./receipt";
import { useChartTip } from "./useChartTip";

export interface SharePart {
  key: string;
  label: string;
  /** Geometry only: the segment's width against the sum of the values. */
  value: number;
  /** The figure as printed: `1,686.5 t`. */
  text: string;
  /** The share as printed: `69.6%`. */
  share?: string;
  colour: ChartColour;
  receipt?: string;
}

export function StackedShare({
  parts,
  caption,
  onOpenReceipt,
  testId = "stacked-share",
}: {
  parts: readonly SharePart[];
  caption: string;
  onOpenReceipt?: OpenReceipt;
  testId?: string;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const refs = useRef(new Map<number, HTMLElement>());
  const reduce = useReducedMotion();
  const asTable = useChartAsTable();
  const { tip, show, hide } = useChartTip(wrap);
  const [active, setActive] = useState<string | null>(null);
  const [focus, setFocus] = useState(0);

  const drawn = parts.filter((p) => p.value > 0);
  const sum = drawn.reduce((total, p) => total + p.value, 0);
  const sentence = (p: SharePart) =>
    `${p.label}: ${p.text}${p.share ? `, ${p.share}` : ""}${canOpen(p.receipt, onOpenReceipt) ? ". Opens the receipt" : ""}`;

  if (asTable) {
    return (
      <div className={chart.chart} data-testid={testId}>
        <ChartTable
          visible
          caption={caption}
          head={["Part", "Figure", "Share"]}
          rows={parts.map((p) => [p.label, p.text, p.share ?? ""])}
        />
      </div>
    );
  }

  const leave = () => {
    setActive(null);
    hide();
  };
  const point = (index: number) => {
    const part = drawn[index];
    if (!part) return;
    setActive(part.key);
    show(refs.current.get(index), part.label, [part.text, ...(part.share ? [part.share] : [])]);
  };
  const move = (index: number) => {
    const next = Math.max(0, Math.min(drawn.length - 1, index));
    setFocus(next);
    refs.current.get(next)?.focus();
  };
  const onKey = (event: KeyboardEvent<HTMLElement>, index: number) => {
    if (event.key === "ArrowRight") move(index + 1);
    else if (event.key === "ArrowLeft") move(index - 1);
    else if (event.key === "Home") move(0);
    else if (event.key === "End") move(drawn.length - 1);
    else if (event.key === "Escape") leave();
    else return;
    event.preventDefault();
  };

  return (
    <div ref={wrap} className={`${chart.chart} ${styles.wrap}`} data-testid={testId}>
      <div
        className={styles.bar}
        role={drawn.some((p) => canOpen(p.receipt, onOpenReceipt)) ? "toolbar" : "group"}
        aria-orientation={drawn.some((p) => canOpen(p.receipt, onOpenReceipt)) ? "horizontal" : undefined}
        aria-label={caption}
      >
        {drawn.map((part, index) => {
          const open = receiptProps(part.receipt, part.key, onOpenReceipt);
          return (
            <motion.div
              key={part.key}
              ref={(node: HTMLDivElement | null) => {
                if (node) refs.current.set(index, node);
                else refs.current.delete(index);
              }}
              role={open.onClick ? "button" : "img"}
              tabIndex={focus === index ? 0 : -1}
              aria-label={sentence(part)}
              className={`${styles.segment} ${active && active !== part.key ? styles.dim : ""} ${open.onClick ? styles.openable : ""}`}
              style={{
                flexGrow: part.value / sum,
                background: colourOf(part.colour),
                transformOrigin: "left",
              }}
              initial={reduce ? false : { scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={reduce ? { duration: 0 } : { duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              data-testid="share-segment"
              data-part={part.key}
              data-receipt={open["data-receipt"]}
              onClick={open.onClick}
              onKeyDown={(event: KeyboardEvent<HTMLElement>) => {
                onKey(event, index);
                if (!event.defaultPrevented) open.onKeyDown?.(event);
              }}
              onFocus={() => {
                setFocus(index);
                point(index);
              }}
              onBlur={leave}
              onPointerEnter={() => {
                point(index);
              }}
              onPointerLeave={leave}
            />
          );
        })}
      </div>
      <ul className={styles.legend} data-testid="share-legend">
        {parts.map((part) => {
          // The figure is printed in the legend as well as on the segment, so it is a way to the
          // receipt in both places (rule 3): a printed tonnage with nothing behind it is the defect.
          const open = receiptProps(part.receipt, part.key, onOpenReceipt);
          return (
            <li key={part.key} className={`${styles.item} ${active === part.key ? styles.itemOn : ""}`}>
              <span className={styles.dotMark} aria-hidden="true" style={{ background: colourOf(part.colour) }} />
              <span className={styles.itemLabel}>{part.label}</span>
              {open.onClick ? (
                <button
                  type="button"
                  className={`${styles.itemValue} ${styles.itemButton} num`}
                  data-receipt={open["data-receipt"]}
                  onClick={open.onClick}
                >
                  {part.text}
                </button>
              ) : (
                <span className={`${styles.itemValue} num`}>{part.text}</span>
              )}
              {part.share ? <span className={`${styles.itemShare} num`}>{part.share}</span> : null}
            </li>
          );
        })}
      </ul>
      <ChartTooltip anchor={tip?.anchor ?? null}>
        <b>{tip?.title}</b>
        {tip?.lines.map((line) => (
          <span key={line} className={styles.tipLine}>
            {line}
          </span>
        ))}
      </ChartTooltip>
    </div>
  );
}
